/**
 * Risk Forecast Page
 *
 * Route: /predictive | Nav: Intelligence > Predictive
 * Category: AI (#8852E0)
 *
 * Shows score overview, risk landscape (pillar scores + top gaps),
 * and an AI-generated forecast narrative.
 */
import React from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import {
  DataTrendingRegular,
  BotRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, narratorApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';

const CATEGORY_COLOR = '#8852E0';

const getFreshnessColor = (freshness: string): 'success' | 'warning' | 'danger' => {
  switch (freshness) {
    case 'live': return 'success';
    case 'stale': return 'warning';
    default: return 'danger';
  }
};

const getSeverityColor = (severity: string): 'danger' | 'warning' | 'important' | 'informative' => {
  switch (severity) {
    case 'Critical': return 'danger';
    case 'High': return 'important';
    case 'Medium': return 'warning';
    default: return 'informative';
  }
};

export const PredictivePage: React.FC = () => {
  const ps = usePageStyles();

  const { data: scores, isLoading: scoresLoading, error: scoresError } = useQuery({
    queryKey: ['scoreSummary'],
    queryFn: () => dashboardApi.getScoreSummary(),
    retry: 1,
  });

  const { data: overview, isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['dashboardOverview'],
    queryFn: () => dashboardApi.getDashboardOverview(),
    retry: 1,
  });

  const { data: forecast, isLoading: forecastLoading } = useQuery({
    queryKey: ['narrator', 'PredictiveAnalytics'],
    queryFn: () => narratorApi.getSummary('PredictiveAnalytics', scores ? {
      governanceScore: scores.governanceScore.value,
      riskScore: scores.riskScore.value,
      complianceScore: scores.complianceScore.value,
    } : undefined),
    enabled: !!scores,
    retry: 1,
    staleTime: 300_000,
  });

  const isLoading = scoresLoading || overviewLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading predictive analytics..." />
      </div>
    );
  }

  // Auth error detection
  const queryError = (scoresError || overviewError) as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isUnauth) {
    return (
      <div className={ps.page}>
        <PageHeader title="Risk Forecast" subtitle="Where might governance risk appear next? Predictive analysis and emerging trend detection." />
        <UnauthenticatedBanner feature="predictive analytics" />
      </div>
    );
  }

  const scoreMetrics = scores ? [
    scores.governanceScore,
    scores.riskScore,
    scores.complianceScore,
    scores.enforcementRate,
    scores.evidenceIntegrity,
  ] : [];

  return (
    <div className={ps.page}>
      <PageHeader title="Risk Forecast" subtitle="Where might governance risk appear next? Predictive analysis and emerging trend detection." icon={<DataTrendingRegular />} iconColor={CATEGORY_COLOR} />

      {/* Score Overview */}
      <div className={ps.metricGrid}>
        {scoreMetrics.map((metric) => (
          <Card key={metric.label}>
            <div className={ps.cardBody}>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{metric.label}</Body2>
                <Title2 style={{ color: metric.value !== null ? CATEGORY_COLOR : tokens.colorNeutralForeground3 }}>
                  {metric.value !== null ? `${Math.round(metric.value)}%` : 'No data yet'}
                </Title2>
                <Badge
                  appearance="filled"
                  color={getFreshnessColor(metric.dataFreshness)}
                  size="small"
                >
                  {metric.dataFreshness === 'live' ? 'Live' : metric.dataFreshness === 'stale' ? 'Stale' : 'No Data'}
                </Badge>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Risk Landscape */}
      {overview && (
        <div className={ps.twoColumn}>
          {/* Pillar Scores */}
          <Card>
            <CardHeader
              header={<Title2>Governance Pillars</Title2>}
              description="Core security posture dimensions"
            />
            <div className={ps.cardBody}>
              <div className={ps.statGrid}>
                <div className={ps.statTile}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Control Coverage</Body2>
                  <Title2>{Math.round(overview.pillars.controlCoverage)}%</Title2>
                </div>
                <div className={ps.statTile}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Enforcement Gaps</Body2>
                  <Title2>{Math.round(overview.pillars.enforcementGaps)}%</Title2>
                </div>
                <div className={ps.statTile}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Evidence Confidence</Body2>
                  <Title2>{Math.round(overview.pillars.evidenceIntegrity)}%</Title2>
                </div>
                <div className={ps.statTile}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Runtime Blind Spots</Body2>
                  <Title2>{Math.round(overview.pillars.runtimeBlindSpots)}%</Title2>
                </div>
              </div>
            </div>
          </Card>

          {/* Top Gaps */}
          <Card>
            <CardHeader
              header={<Title2>Top Gaps</Title2>}
              description="Priority areas for improvement"
            />
            <div className={ps.cardBody}>
              {overview.topGaps && overview.topGaps.length > 0 ? (
                <div className={ps.rowList}>
                  {overview.topGaps.slice(0, 8).map((gap, idx) => (
                    <div key={idx} className={ps.rowItem}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                          <Badge appearance="filled" color={getSeverityColor(gap.severity)} size="small">
                            {gap.severity}
                          </Badge>
                          <Body1 style={{ fontWeight: 500 }}>{gap.description}</Body1>
                        </div>
                        <Body2 style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase100 }}>
                          {gap.recommendedAction}
                        </Body2>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Body1 className={ps.emptyState}>No gaps identified.</Body1>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* AI Forecast */}
      <Card>
        <CardHeader
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Title2>AI Forecast</Title2>
              <Badge appearance="tint" size="small" style={{ backgroundColor: `${CATEGORY_COLOR}20`, color: CATEGORY_COLOR }}>
                <BotRegular style={{ fontSize: '12px', marginRight: '4px' }} />
                AI Generated
              </Badge>
            </div>
          }
          description="Trend analysis and security posture forecast"
        />
        <div className={ps.cardBody}>
          {forecastLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalL }}>
              <Spinner size="small" label="Generating forecast..." />
            </div>
          ) : forecast?.summary ? (
            <Body1 style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
              {forecast.summary}
            </Body1>
          ) : (
            <Body1 className={ps.emptyState}>
              AI forecast unavailable. Configure the AI Narrator to enable predictive insights.
            </Body1>
          )}
        </div>
      </Card>
    </div>
  );
};
